import React, {useState} from 'react';
import {useHistory} from 'react-router-dom';

import {FancyPageSecondarySidebar} from './FancyPageSecondarySidebar';
import {
  FancyPageSidebar,
  FancyPageSidebarItem,
  FancyPageSidebarItemButton,
  FancyPageSidebarItemSidebarMenu,
} from './FancyPageSidebar';

type FancyPageProps = {
  baseUrl: string;
  /**
   * Slug of the active item on the sidebar.
   * If undefined, no item will be active.
   */
  activeSlug?: string;
  items: FancyPageSidebarItem[];
  /**
   * If specified, children will be rendered in place of
   * calling `.render` of the active item.
   */
  children?: React.ReactNode;
};

export const FancyPage = React.memo(
  ({baseUrl, activeSlug, items, children}: FancyPageProps) => {
    const history = useHistory();
    const [secondarySidebarConfig, setSecondarySidebarConfig] = useState<{
      isOpen: boolean;
      title: string;
      items: FancyPageSidebarItemButton[];
    }>({isOpen: false, title: '', items: []});

    // Handle sidebar menu clicks
    const handleSidebarMenuClick = React.useCallback(
      (menuItem: FancyPageSidebarItem) => {
        if (menuItem.type === 'sidebarMenu') {
          // Check if this sidebarMenu is already selected (i.e., one of its menu items is active)
          setSecondarySidebarConfig(prev => {
            const isCurrentlyOpen = prev.isOpen && prev.title === menuItem.name;
            // If clicking on the already selected sidebarMenu while it's open, keep it open
            if (isCurrentlyOpen) {
              return prev;
            }
            // Navigate to the default item (first item in the menu)
            const defaultItem = menuItem.menu[0];
            if (defaultItem && defaultItem.slug) {
              history.push(`${baseUrl}/${defaultItem.slug}`);
            }
            return {
              isOpen: true,
              title: menuItem.name,
              items: menuItem.menu,
            };
          });
        }
      },
      [baseUrl, history]
    );

    // Handle clicks on non-sidebarMenu items
    const handleNonMenuItemClick = React.useCallback(
      (clickedSlug?: string) => {
        // Check if the clicked slug is part of any sidebarMenu
        const isPartOfSidebarMenu = items.some(item => {
          if (item.type === 'sidebarMenu' && item.menu) {
            return item.menu.some(
              menuItem =>
                menuItem.slug === clickedSlug ||
                menuItem.additionalSlugs?.includes(clickedSlug ?? '')
            );
          }
          return false;
        });

        // Only close if the clicked item is not part of any sidebarMenu
        if (!isPartOfSidebarMenu) {
          setSecondarySidebarConfig(prev => ({...prev, isOpen: false}));
        }
      },
      [items]
    );

    const activeItem =
      activeSlug === undefined
        ? undefined
        : items.find(item => {
            // Check if the item's own slug or additional slugs match
            if ('slug' in item && item.slug === activeSlug) {
              return true;
            }
            if (
              'additionalSlugs' in item &&
              item.additionalSlugs?.includes(activeSlug)
            ) {
              return true;
            }
            // For sidebarMenu items, check if any menu item matches the active slug
            if (item.type === 'sidebarMenu' && item.menu) {
              return item.menu.some(
                menuItem =>
                  menuItem.slug === activeSlug ||
                  menuItem.additionalSlugs?.includes(activeSlug)
              );
            }
            return false;
          });

    // Determine selected item in secondary sidebar
    const selectedSecondaryItem = secondarySidebarConfig.items.find(
      item =>
        item.slug === activeSlug ||
        item.additionalSlugs?.includes(activeSlug ?? '')
    );

    // Check if we should keep the secondary sidebar open based on the active slug
    React.useEffect(() => {
      // Find if any sidebarMenu contains the active slug
      const sidebarMenuWithActiveSlug = items.find(item => {
        if (item.type === 'sidebarMenu' && item.menu) {
          return item.menu.some(
            menuItem =>
              menuItem.slug === activeSlug ||
              menuItem.additionalSlugs?.includes(activeSlug ?? '')
          );
        }
        return false;
      }) as FancyPageSidebarItemSidebarMenu | undefined;

      if (sidebarMenuWithActiveSlug && activeSlug) {
        // If we found a sidebarMenu that contains the active slug, open it
        setSecondarySidebarConfig({
          isOpen: true,
          title: sidebarMenuWithActiveSlug.name,
          items: sidebarMenuWithActiveSlug.menu,
        });
      } else {
        // Otherwise, close the sidebar
        setSecondarySidebarConfig(prev => ({...prev, isOpen: false}));
      }
    }, [activeSlug, items]);

    return (
      <div className="fancy-page">
        <FancyPageSidebar
          items={items}
          selectedItem={activeItem}
          baseUrl={baseUrl}
          onSidebarMenuClick={handleSidebarMenuClick}
          onNonMenuItemClick={handleNonMenuItemClick}
        />
        {!secondarySidebarConfig.isOpen && (
          <main id="main-content" className="fancy-page__content">
            {children ??
              (activeItem && 'render' in activeItem && activeItem.render?.())}
          </main>
        )}
        {secondarySidebarConfig.isOpen && (
          <div className="fancy-page__main-area">
            <div className="fancy-page__secondary-header">
              {secondarySidebarConfig.title}
            </div>
            <div className="fancy-page__main-area-content">
              <FancyPageSecondarySidebar
                items={secondarySidebarConfig.items}
                selectedItem={selectedSecondaryItem}
                baseUrl={baseUrl}
                isOpen={secondarySidebarConfig.isOpen}
                onClose={() =>
                  setSecondarySidebarConfig(prev => ({...prev, isOpen: false}))
                }
              />
              <main
                id="main-content"
                className={
                  'fancy-page__content fancy-page__content--secondary-sidebar-open'
                }>
                {children ??
                  (activeItem &&
                    'render' in activeItem &&
                    activeItem.render?.())}
              </main>
            </div>
          </div>
        )}
      </div>
    );
  }
);
FancyPage.displayName = 'FancyPage';
